import React, { useState, useEffect, useRef, memo } from "react";
import { Platform } from "react-native";
import {
  View,
  Text,
  FlatList,
  Stack,
  HStack,
  useMediaQuery,
} from "native-base";

import { ProductCardSkeleton } from "@components/ProductCardSkeleton";
import { SearchBar } from "@components/SearchBar";
import { Loading } from "@components/Loading";

import { Config } from "@configs/Config";
import { useAuth } from "@contexts/Auth";
import { FastAccessFilterEnum } from "@enums/FastAccessFilterEnum";
import { CartProductTypeEnum } from "@enums/CartProductTypeEnum";
import { RequiredTypeEnum } from "@enums/RequiredTypeEnum";
import {
  useCommercialPolicyProductsLazy,
  refactorResult,
} from "@hooks/useCommercialPolicyProducts";
import { AssortmentInputType } from "@types/AssortmentInputType";
import { CommercialPolicyProductsType } from "@types/CommercialPolicyProductsType";
import { ProductInputType } from "@types/ProductInputType";
import { UnexpectedError } from "../UnexpectedError";
import { Pagination } from "../Pagination";
import { ProductCard } from "../ProductCard";

import { styles } from "./styles";
import { searchFilterVar } from "@components/FastAccessCard/FastAccessCard";
import { makeVar, useReactiveVar } from "@apollo/client";
import { useFilter } from "@contexts/FilterContext";
import { FilterIconMobile, fastAccessModalVar } from "@components/FastAccessCard/FilterIconMobile";
import { TagAlertOutdatedPrice } from "@components/TagAlertOutdatedPrice";
import { useWholesalers } from "@hooks/useWholesalers";
import { useWholesalersStorage } from "@hooks/useWholesalersStorage";

interface ProductsListProps {
  flex?: string | number;
  paginationInfinite?: boolean;
}

export const loadingFilterVar = makeVar(false);

let totalProductsPerPage = Config.TOTAL_PRODUCTS_EXHIBITED
  ? parseInt(Config.TOTAL_PRODUCTS_EXHIBITED)
  : 20;

const totalOffersPerPageMobile = 4;

export const ProductList: React.FC<ProductsListProps> = memo((props) => {
  const { paginationInfinite = false } = props;
  const { user } = useAuth();
  const nameIndustry = user?.getNameIndustryStore();
  const fastAccessFilter = useReactiveVar(searchFilterVar);
  const { setCurrentTabView } = useFilter();

  const [isLargeScreen, isMediumScreen, isSmallScreen] = useMediaQuery([
    {
      minWidth: 1780,
    },
    {
      maxWidth: 960,
    },
    {
      maxWidth: 768,
    },
  ]);

  function setTabViewInFocus(tabView: string) {
    setCurrentTabView(tabView)
  }

  if (isSmallScreen) {
    setTabViewInFocus('productList');
  }

  const numColumns =
    isSmallScreen || isMediumScreen ? 2 : isLargeScreen ? 4 : 3;
  const cardWidth = isSmallScreen ? "50%" : "33%";

  if (isSmallScreen) {
    totalProductsPerPage = totalOffersPerPageMobile;
  }

  const [searchText, setSearchText] = useState("");
  const [offset, setOffset] = useState<number>(0);
  const [total, setTotal] = useState(1);
  const [endOfList, setEndOfList] = useState(false);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const { wholesalers, loading: wholesalersLoading } = useWholesalersStorage()
  const myRef = useRef<null | HTMLDivElement>(null);
  const executeScroll = () => myRef.current?.scrollIntoView();

  const [focusItemList, setFocusItemList] = useState<
    CommercialPolicyProductsType[]
  >([]);
  const [productsList, setProductsList] = useState<
    CommercialPolicyProductsType[]
  >([]);

  const [
    query,
    { data, loading: commercialPolicyProductsLoading, error, refetch },
  ] = useCommercialPolicyProductsLazy({
    wholesalerBranchCode: wholesalers[0]?.code ?? "",
    limit: totalProductsPerPage,
    offset: 0,
    requiredType: [RequiredTypeEnum.NON_REQUIRED],
    product: {
      filter: "",
    },
    fastAccessFilter
  });

  fastAccessModalVar(false)

  useEffect(() => {
    if (wholesalersLoading) return;

    if (fastAccessFilter !== undefined && !isFirstLoading) {
      loadingFilterVar(true);
      updateData(offset);
      return;
    }

    (async () => {
      const { data } = await query();
      setTotal(data.commercialPolicyProducts.pageInfo.totalCount);
      result(data.commercialPolicyProducts);
      setIsFirstLoading(false);
    })();
  }, [offset, wholesalersLoading]);

  useEffect(() => {
    if (isFirstLoading) return;

    setFocusItemList([]);
    loadingFilterVar(true);
    setEndOfList(false);

    // TODO: This logic should be rethought when the filter is applied in the web
    if (offset === 0 || !paginationInfinite) {
      updateData();
      return;
    }

    setOffset(0);
  }, [fastAccessFilter]);

  if (wholesalersLoading) {
    return <Loading />;
  }

  if (error) {
    return <UnexpectedError consoleError={error.message} />;
  }

  async function updateData(newOffset: number = 0) {
    setIsFirstLoading(false);

    const hasProgressive = fastAccessFilter?.single?.includes(
      FastAccessFilterEnum.HAS_PROGRESSIVE
    );
    const hasCampaign = fastAccessFilter?.single?.includes(
      FastAccessFilterEnum.CAMPAIGN
    );

    let assortment: AssortmentInputType = {
      ids: fastAccessFilter.assortment
    }

    if (assortment.ids?.length === 0) {
      assortment = {
        ids: user.getAssortments().map((assortment) => Number(assortment.id)),
      };
    }

    let productDivision: AssortmentInputType = {
      ids: fastAccessFilter.division_product
    }

    const brand = {
      ids: fastAccessFilter.family
    }

    const filter: ProductInputType = {
      filter: searchText,
      assortment,
      productDivision,
      brand
    };

    const filteredProducts = await refetch({
      product: filter,
      offset: newOffset,
      limit: totalProductsPerPage,
      hasProgressive: hasProgressive,
      hasCampaign: hasCampaign,
    });

    if (
      filteredProducts.data.commercialPolicyProducts.pageInfo.pageCount <
      totalProductsPerPage
    ) {
      setEndOfList(true);
    }

    if (
      filteredProducts.data.commercialPolicyProducts.pageInfo.pageCount == 0
    ) {
      setEndOfList(true);
      loadingFilterVar(false);
      setTotal(
        filteredProducts.data.commercialPolicyProducts.pageInfo.totalCount
      );

      return;
    }

    setTotal(
      filteredProducts.data.commercialPolicyProducts.pageInfo.totalCount
    );

    result(filteredProducts.data.commercialPolicyProducts);
  }

  function result(products: CommercialPolicyProductsType[]) {
    if (!isFirstLoading && Platform.OS == "web") {
      executeScroll();
    }

    const result = refactorResult(products, nameIndustry);
    const startItemList = result.slice(0, totalProductsPerPage);

    if (paginationInfinite) {
      setFocusItemList((prev) => [...prev, ...startItemList]);
    } else {
      setFocusItemList(startItemList);
    }

    setProductsList(result);
    loadingFilterVar(false);
  }

  function searchByFilters(searchFilter?: string) {
    loadingFilterVar(true);
    setEndOfList(false);

    if (paginationInfinite) {
      setFocusItemList([]);
    }

    updateData();
  }

  function renderItem({
    item,
    index,
  }: {
    item: CommercialPolicyProductsType;
    index: number;
  }) {
    return (
      <View
        flex={1}
        maxW={cardWidth}
        key={index}
        alignItems={["center", "center", "center", "baseline"]}
      >
        <ProductCard
          product={item}
          tab={CartProductTypeEnum.DEFAULT}
          isLoading={loadingFilterVar()}
        />
      </View>
    );
  }

  return (
    <Stack direction={"column"} flex={1} mt={4}>
      <View ref={myRef}></View>
      <HStack
        space={4}
        pb={4}
        px={["3", "3", 0]}
        direction={["column", "column", "column", "row"]}
        justifyContent={["center", "center", "space-between"]}
        alignItems={["center", "baseline"]}
        bgColor={["white", ""]}
        _dark={{
          bgColor: ["muted.800", ""],
        }}
        borderBottomWidth={[2, 0]}
        borderColor="coolGray.200"
        borderWidth="0"
      >
        {!isSmallScreen && (
          <Text
            style={styles.title}
            color={"muted.800"}
            _dark={{
              color: "muted.300",
            }}
          >
            Produtos
          </Text>
        )}
        <View w={["full", "full", "xs"]}>
          <SearchBar
            variant={isSmallScreen ? 'outline' : ''}
            size={['md', 'xs']}
            placeholder={isSmallScreen ? "Pesquisar produtos" : "O que você procura?"}
            value={searchText}
            onChangeText={setSearchText}
            onKeyPress={(event) => {
              if (event.nativeEvent.key !== "Enter") {
                return;
              }

              searchByFilters();
            }}
            onPress={() => searchByFilters()}
          />
        </View>
      </HStack>
      <TagAlertOutdatedPrice mb={4} />
      {isSmallScreen && <View><FilterIconMobile /></View>}
      <View
        alignSelf={["center", "center", "flex-start"]}
        flex={props.flex}
        w={["100%"]}
      >
        {commercialPolicyProductsLoading ? (
          <FlatList
            data={[1, 2, 3, 4, 6, 9]}
            ItemSeparatorComponent={() => <View style={{ height: 20 }} />}
            renderItem={() => (
              <View
                flex={1}
                maxW={cardWidth}
                alignItems={["center", "center", "center", "baseline"]}
              >
                <ProductCardSkeleton />
              </View>
            )}
            key={numColumns}
            numColumns={numColumns}
          />
        ) : (
          <>
            {total > 0 ? (
              <>
                <FlatList
                  key={numColumns}
                  data={focusItemList}
                  columnWrapperStyle={{
                    justifyContent: isSmallScreen
                      ? "space-between"
                      : "flex-start",
                  }}
                  contentContainerStyle={{
                    justifyContent: "space-around",
                    paddingBottom: 100,
                  }}
                  showsHorizontalScrollIndicator={false}
                  renderItem={renderItem}
                  keyExtractor={(item) => item.product.code}
                  numColumns={numColumns}
                  ItemSeparatorComponent={() => <View style={{ height: 20 }} />}
                  onEndReached={async ({ distanceFromEnd }) => {
                    if (!paginationInfinite) return;
                    if (distanceFromEnd == 0 || endOfList) return;
                    if (loadingFilterVar()) return;

                    setOffset(offset + totalProductsPerPage);
                  }}
                  onEndReachedThreshold={0.2}
                  ListFooterComponent={() =>
                    loadingFilterVar() ? <Loading /> : null
                  }
                />

                {!paginationInfinite && (
                  <Pagination
                    pageCount={totalProductsPerPage}
                    stylePaginationButton={styles.paginationButton}
                    stylePaginationButtonActive={styles.paginationButtonActive}
                    itemList={productsList}
                    setOffset={setOffset}
                    total={total}
                  />
                )}
              </>
            ) : (
              <View>
                <Text style={styles.textDescription} paddingLeft={[5, 0]}>
                  Não há dados a serem exibidos
                </Text>
              </View>
            )}
          </>
        )}
      </View>
    </Stack>
  );
});
